import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CalendarCheck, Clock, MapPin, Medal, Users } from 'lucide-react';
import { courtDisplayName } from '../../engine';
import { useLocale, useT } from '../../i18n';
import {
  fetchMyActivities,
  fetchMyBookings,
  fetchMyMatches,
  type MyHistoryActivity,
  type MyHistoryBooking,
  type MyHistoryMatch,
} from '../../lib/api/myHistory';
import { venuePaymentSubject } from '../../lib/api/memberPayment';
import { PayForBooking } from '../booking/PayForBooking';
import { CancelOwnBooking } from './CancelOwnBooking';
import { localMs, relativeDayLabel, splitUpcomingPast } from './memberData';
import { Card, EmptyState } from './mobileUi';

// ─────────────────────────────────────────────────────────────────────
// «ALLE KLUBBER» — the person's own history across every club (#760).
//
// A segment beside «Kommende» and «Tidligere» in Reservasjoner, and beside
// «Kommende» and «Spilt» in Kamper. The club segments are unchanged and still
// read the ACTIVE club; this reads the account-scoped `/me/*` endpoints, which
// answer with the person's own rows from every club, each labelled with the
// club it belongs to.
//
// Read only when the segment is opened, not when the tab is: most visits never
// ask, and a tab should not cost three requests nobody looked at.
//
// What it shows is exactly what the server projects — the person's own
// bookings, their own registrations, their own matches with the others named.
// No court nicknames (those live in the club's configuration, which this does
// not read).
//
// ── A GUEST'S BOOKING IS PAID AND CANCELLED HERE (#833 slice 3b) ──
//
// A member pays inside the club, where the booking and its price are the
// club's own, so a member's row here only says it is unpaid. A GUEST has no
// club to go into: the venue's door is the only way they have to the booking.
// So a row the server says was made at the guest price (`priceTier: 'guest'`)
// is marked «Gjest», and — while it is upcoming — carries the member app's own
// «Betal nå» (with the hold counted down from the server's `expiresInMs`) and
// «Avbestill», both through `/venues/:slug/bookings/:id/…`. The slug is the
// club label the server put on the row, which is the venue's own.
//
// THE TERMS BEFORE THE BUTTON (#1372): the row's `cancellable` is the club's
// own answer, from `/me/bookings` — the deadline, what cancelling now gives
// back, and whether it is still open — so the guest reads «Gratis avbestilling
// til …», or «for sent», before pressing anything. Where the server sent none
// (the payment is still moving), and once the guest has paid HERE (the read's
// amount is then stale), the dialog states the rule instead, and the command's
// answer decides.
// ─────────────────────────────────────────────────────────────────────

type Loaded<R> = { status: 'loading' } | { status: 'failed' } | { status: 'ready'; data: R };

/**
 * Load once per mount and per retry. `load` answers null when ANY read went
 * unanswered — a half-loaded history would read as «you have less than you
 * do», so it is a failure with a retry instead.
 */
function useHistory<R>(load: () => Promise<R | null>) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Loaded<R>>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    void load().then((data) => {
      if (cancelled) return;
      setState(data == null ? { status: 'failed' } : { status: 'ready', data });
    });
    return () => {
      cancelled = true;
    };
    // `load` is one of the two module-level loaders below, so it never changes.
  }, [attempt, load]);
  return { state, retry: () => setAttempt((a) => a + 1) };
}

function Lead({ children }: { children: ReactNode }) {
  return <p className="text-[12px] text-ink-3 px-1 -mt-1">{children}</p>;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-1 pt-1 @wide:col-span-2">
      {children}
    </div>
  );
}

function ClubTag({ name }: { name: string }) {
  return (
    <span
      className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 truncate max-w-[45%]"
      style={{ background: '#eef0ea', color: '#0f1611' }}
      data-testid="history-club"
    >
      {name}
    </span>
  );
}

function Loading() {
  const t = useT();
  return (
    <div className="px-1 py-6 text-center text-[13px] text-ink-3" role="status">
      {t('mobile.history.loading')}
    </div>
  );
}

function Failed({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="rounded-2xl px-4 py-6 text-center" style={{ background: '#fff' }}>
      <div className="text-[13px] font-semibold text-ink-2">{t('mobile.history.failed')}</div>
      <button type="button" className="btn-ghost text-xs mt-2" onClick={onRetry}>
        {t('mobile.history.retry')}
      </button>
    </div>
  );
}

const timeRange = (start: string | null, end: string | null) =>
  start ? (end ? `${start}–${end}` : start) : '';

/** An across-clubs activity the server DID date — the only kind this list sorts. */
type DatedActivity = MyHistoryActivity & { when: MyHistoryActivity['when'] & { date: string } };

type Item =
  | { kind: 'booking'; key: string; startMs: number; endMs: number; b: MyHistoryBooking }
  | { kind: 'activity'; key: string; startMs: number; endMs: number; a: DatedActivity };

async function loadReservations() {
  const [bookings, activities] = await Promise.all([fetchMyBookings(), fetchMyActivities()]);
  if (bookings.failed || activities.failed) return null;
  return { bookings: bookings.items, activities: activities.items };
}

/** Reservasjoner › Alle klubber: own bookings and sign-ups, in every club. */
export function AcrossClubsReservations() {
  const t = useT();
  const { state, retry } = useHistory(loadReservations);

  const { upcoming, past } = useMemo(() => {
    const { bookings, activities } =
      state.status === 'ready' ? state.data : { bookings: [], activities: [] };
    const items: Item[] = [
      ...bookings.map((b) => ({
        kind: 'booking' as const,
        // A booking id is unique within ITS club only.
        key: `b:${b.club.slug}:${b.id}`,
        startMs: localMs(b.date, b.startTime),
        endMs: localMs(b.date, b.endTime || b.startTime),
        b,
      })),
      // An activity the server could not date is left out here, as before:
      // this list is split by time, and «Tid ikke satt» has no side of «now».
      // Narrowed by the type guard rather than asserted with `!` (#1337).
      ...activities
        .filter((a): a is DatedActivity => !!a.when.date)
        .map((a) => ({
          kind: 'activity' as const,
          key: `a:${a.club.slug}:${a.id}`,
          startMs: localMs(a.when.date, a.when.start),
          endMs: localMs(a.when.date, a.when.end || a.when.start),
          a,
        })),
    ];
    return splitUpcomingPast(items);
  }, [state]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'failed') return <Failed onRetry={retry} />;
  return (
    <div className="space-y-2" data-testid="across-clubs-reservations">
      <Lead>{t('mobile.history.lead')}</Lead>
      {upcoming.length + past.length === 0 ? (
        <EmptyState
          icon={<CalendarCheck size={22} />}
          title={t('mobile.history.empty')}
          hint={t('mobile.history.emptyHint')}
        />
      ) : (
        <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
          {upcoming.length > 0 && <SectionLabel>{t('mobile.reservations.upcoming')}</SectionLabel>}
          {upcoming.map((it) => (
            <ReservationRow key={it.key} it={it} upcoming />
          ))}
          {past.length > 0 && <SectionLabel>{t('mobile.reservations.past')}</SectionLabel>}
          {past.map((it) => (
            <ReservationRow key={it.key} it={it} upcoming={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReservationRow({ it, upcoming }: { it: Item; upcoming: boolean }) {
  const t = useT();
  if (it.kind === 'booking') return <BookingRow b={it.b} upcoming={upcoming} />;
  const a = it.a;
  const waitlist = a.registration.status === 'waitlist';
  return (
    <Card accent="#84cc16">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {relativeDayLabel(a.when.date)}
          {a.when.days > 1 ? ` (${t('mobile.history.days', { count: a.when.days })})` : ''}
          {a.when.start ? ` · ${timeRange(a.when.start, a.when.end)}` : ''}
        </span>
        <ClubTag name={a.club.name} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          <Users size={14} className="text-ink-3" /> {a.name || t('mobile.common.activity')}
        </div>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: waitlist ? '#fef9c3' : '#f7fdde', color: '#0f1611' }}
        >
          {waitlist ? t('mobile.common.waitlist') : t('mobile.common.signedUp')}
        </span>
      </div>
      {a.registration.asPartner && (
        <div className="mt-1 text-[12px] text-ink-2">{t('mobile.history.asPartner')}</div>
      )}
    </Card>
  );
}

/** «Gjest» — the booking was made at the venue's guest price (#833). */
function GuestTag() {
  const t = useT();
  return (
    <span
      className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
      style={{ background: '#fef9c3', color: '#854d0e' }}
      data-testid="history-guest"
    >
      {t('mobile.history.guest')}
    </span>
  );
}

function BookingRow({ b, upcoming }: { b: MyHistoryBooking; upcoming: boolean }) {
  const t = useT();
  /** The server said paid, after paying right here (#284) — until the next load. */
  const [paidHere, setPaidHere] = useState(false);
  /** Cancelled here, on the server's word (#1135) — what went back, until the next load. */
  const [cancelledHere, setCancelledHere] = useState<number | null>(null);
  const guest = b.priceTier === 'guest';
  const subject = venuePaymentSubject(b.club.slug);
  const summary = `${courtDisplayName(b.court, {})} · ${relativeDayLabel(b.date)} ${timeRange(
    b.startTime,
    b.endTime,
  )}`;
  const unpaid = upcoming && b.paymentStatus === 'pending' && !paidHere && cancelledHere === null;
  return (
    <Card accent="#c2f02f">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {relativeDayLabel(b.date)} · {timeRange(b.startTime, b.endTime)}
        </span>
        <span className="flex items-center gap-1 min-w-0 justify-end">
          {guest && <GuestTag />}
          <ClubTag name={b.club.name} />
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="text-[14px] font-semibold text-ink truncate flex items-center gap-1.5">
          <Clock size={14} className="text-ink-3" />
          {b.title || t('mobile.reservations.courtBooking')}
        </div>
        <span className="font-mono text-[10px] text-ink-2 flex items-center gap-1 flex-shrink-0">
          <MapPin size={11} /> {courtDisplayName(b.court, {})}
        </span>
      </div>
      {unpaid &&
        (guest ? (
          // THE GUEST'S WAY TO PAY: the member app's own «Betal nå», sent to the
          // venue's door. The hold is the server's — `expiresInMs` from this
          // read, re-read by the component itself whenever it has to be.
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[12px] text-ink-2">{t('mobile.reservations.unpaid')}</span>
            <PayForBooking
              booking={b}
              subject={subject}
              summary={summary}
              {...(b.expiresInMs === null ? {} : { expiresInMs: b.expiresInMs })}
              onPaid={() => setPaidHere(true)}
            />
          </div>
        ) : (
          <div className="mt-1 text-[12px] text-ink-2">{t('mobile.reservations.unpaid')}</div>
        ))}
      {paidHere && cancelledHere === null && (
        <div className="mt-2 text-[12px] font-semibold" data-testid="reservation-paid">
          {t('mobile.reservations.paid')}
        </div>
      )}
      {guest &&
        upcoming &&
        (cancelledHere !== null ? (
          <div className="mt-2 text-[12px] font-semibold" data-testid="reservation-cancelled">
            {cancelledHere > 0
              ? t('mobile.reservations.cancel.doneRefund', { amount: String(cancelledHere / 100) })
              : t('mobile.reservations.cancel.done')}
          </div>
        ) : (
          // THE SERVER'S TERMS (#1372), unless the guest paid here since the
          // read: its refund (nothing, then) would no longer be true, so the
          // dialog states the rule and the venue's command decides.
          <CancelOwnBooking
            id={b.id}
            subject={subject}
            {...(b.cancellable && !paidHere ? { cancellable: b.cancellable } : {})}
            paid={b.paymentStatus === 'paid' || paidHere}
            summary={summary}
            onCancelled={setCancelledHere}
          />
        ))}
    </Card>
  );
}

async function loadMatches() {
  const matches = await fetchMyMatches();
  return matches.failed ? null : matches.items;
}

/** Kamper › Alle klubber: own single matches, in every club, newest first. */
export function AcrossClubsMatches() {
  const t = useT();
  const { state, retry } = useHistory(loadMatches);
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'failed') return <Failed onRetry={retry} />;
  const matches = state.data;
  return (
    <div className="space-y-2" data-testid="across-clubs-matches">
      <Lead>{t('mobile.history.matchesLead')}</Lead>
      {matches.length === 0 ? (
        <EmptyState
          icon={<Medal size={22} />}
          title={t('mobile.history.emptyMatches')}
          hint={t('mobile.history.emptyMatchesHint')}
        />
      ) : (
        <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
          {matches.map((m) => (
            <MatchRow key={`${m.club.slug}:${m.id}`} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchRow({ m }: { m: MyHistoryMatch }) {
  const { t, formatDate } = useLocale();
  const won = m.won;
  const us = m.partner?.name
    ? t('mobile.history.youAnd', { name: m.partner.name })
    : t('mobile.history.you');
  const them = m.opponents
    .map((o) => o.name)
    .filter(Boolean)
    .join(' & ');
  return (
    <Card accent={won == null ? '#8a948c' : won ? '#84cc16' : '#f43f5e'}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {t('mobile.reservations.singleMatch')}
          {m.playedAt
            ? ` · ${formatDate(new Date(m.playedAt), { day: 'numeric', month: 'short' })}`
            : ''}
        </span>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
          style={{
            background: won == null ? '#eef0ea' : won ? '#f7fdde' : '#ffe4e6',
            color: won == null ? '#4a544c' : won ? '#3f6212' : '#be123c',
          }}
        >
          {won == null
            ? t('mobile.matches.playedTag')
            : won
              ? t('mobile.matches.wonTag')
              : t('mobile.matches.lostTag')}
        </span>
      </div>
      {/* Two lines rather than three columns: at phone width a name beside the
          score beside two more names truncates all of them. */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-[13px] font-semibold text-ink truncate">{us}</div>
        <div className="font-mono font-bold text-[14px] text-ink tabular-nums flex-shrink-0">
          {m.sets.map((s) => `${s.mine}-${s.theirs}`).join('  ')}
        </div>
      </div>
      {them && (
        <div className="text-[13px] text-ink-2 truncate">
          {t('mobile.history.versus', { names: them })}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <ClubTag name={m.club.name} />
        {m.eloChange != null && (
          <span
            className="text-[11px] font-mono font-bold flex-shrink-0"
            style={{ color: m.eloChange >= 0 ? '#3f6212' : '#be123c' }}
          >
            {m.eloChange >= 0 ? '+' : ''}
            {Math.round(m.eloChange)} ELO
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * Reservasjoner or Kamper with NO club chosen (#760, owner decision
 * 2026-11-02): «a player with no club sees the cross-club list» — not «Velg en
 * klubb». The person's own history is theirs whichever club is in context, so
 * the tab answers with it, and choosing a club stays one tap away for the
 * club's own segments.
 *
 * Only for an account that has a person to ask about; `MobileApp` decides
 * that, as it does for the «Alle klubber» segment.
 */
export function AcrossClubsWithoutClub({
  tab,
  onChooseClub,
}: {
  tab: 'reservations' | 'matches';
  onChooseClub: () => void;
}) {
  const t = useT();
  return (
    <div className="px-4 pb-4 space-y-3" data-testid="across-clubs-without-club">
      <h1
        className="font-display font-semibold text-ink leading-tight pt-2"
        style={{ fontSize: 26 }}
      >
        {tab === 'reservations' ? t('mobile.reservations.title') : t('mobile.common.matches')}
      </h1>
      <div
        className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
        style={{ background: '#fff' }}
      >
        <p className="text-[12px] text-ink-3">{t('mobile.history.noClubLead')}</p>
        <button type="button" className="btn-ghost text-xs flex-shrink-0" onClick={onChooseClub}>
          {t('mobile.history.chooseClub')}
        </button>
      </div>
      {tab === 'reservations' ? <AcrossClubsReservations /> : <AcrossClubsMatches />}
    </div>
  );
}
